// src/automation/components/RapidAudit.js
//
// Audit view for a RAPID task: pick a date (newest first), see every cycle's
// full prioritized / deprioritized agent-queue changes for that day. Data from
// the per-date audit files the RAPID engine writes (get_audit_dates / get_audit).

import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAuditDates, getAudit } from "../api/automation_data";

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function ChangeTable({ title, tone, cols, rows }) {
  return (
    <div className="auto-cfg-block">
      <h4 className="auto-cfg-block-title">
        <span className={`auto-audit-dot auto-audit-dot--${tone}`} /> {title} ({rows.length})
      </h4>
      {rows.length === 0 ? (
        <div className="auto-empty">None</div>
      ) : (
        <div className="auto-table-wrap">
          <table className="auto-table">
            <thead>
              <tr>{cols.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  {cols.map((c) => (
                    <td key={c.key} className={c.mono ? "auto-table-mono" : ""}>
                      {r[c.key] == null || r[c.key] === "" ? "—" : String(r[c.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function RapidAudit({ taskName }) {
  const [date, setDate] = useState(null);

  const { data: dates = [], isLoading: datesLoading } = useQuery({
    queryKey: ["auditDates", taskName],
    queryFn: () => getAuditDates(taskName),
    staleTime: 30 * 1000,
  });

  useEffect(() => {
    if (!date && dates.length) setDate(dates[0]);
  }, [dates, date]);

  const { data: day, isLoading: dayLoading } = useQuery({
    queryKey: ["audit", taskName, date],
    queryFn: () => getAudit(taskName, date),
    enabled: !!date,
    staleTime: 30 * 1000,
  });

  if (datesLoading) return <div className="auto-empty">Loading audit…</div>;
  if (dates.length === 0) {
    return (
      <div className="auto-empty">
        No audit history yet. Changes appear here after this RAPID task runs
        (each cycle logs what it prioritized / deprioritized).
      </div>
    );
  }

  const cycles = day?.cycles || [];
  const totalPri = cycles.reduce((n, c) => n + (c.prioritized?.length || 0), 0);
  const totalDep = cycles.reduce((n, c) => n + (c.deprioritized?.length || 0), 0);

  return (
    <div>
      <div className="auto-audit-bar">
        <label className="auto-eb-field" style={{ maxWidth: 220 }}>
          <span className="auto-eb-field-label">Date</span>
          <select className="auto-eb-input" value={date || ""} onChange={(e) => setDate(e.target.value)}>
            {dates.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <div className="auto-audit-summary">
          <span>{cycles.length} cycle{cycles.length === 1 ? "" : "s"}</span>
          <span className="auto-audit-pri">▲ {totalPri} prioritized</span>
          <span className="auto-audit-dep">▼ {totalDep} deprioritized</span>
        </div>
      </div>

      {dayLoading ? (
        <div className="auto-empty">Loading…</div>
      ) : cycles.length === 0 ? (
        <div className="auto-empty">No changes recorded on {date}.</div>
      ) : (
        cycles.map((c, i) => (
          <div className="auto-collapse auto-collapse--open" key={i}>
            <div className="auto-collapse-head" style={{ cursor: "default" }}>
              <span className="auto-collapse-title">Cycle at {fmtTime(c.timestamp)}</span>
              <span className="auto-collapse-sub">
                {c.dry_run ? "dry run · " : ""}
                {(c.prioritized?.length || 0)}▲ / {(c.deprioritized?.length || 0)}▼
                {c.nightly_reset ? ` · reset ${c.nightly_reset}` : ""}
              </span>
            </div>
            <div className="auto-collapse-body">
              <ChangeTable
                title="Prioritized"
                tone="pri"
                cols={[
                  { key: "login", label: "Agent", mono: true },
                  { key: "queue", label: "Queue", mono: true },
                  { key: "region", label: "Region" },
                  { key: "tenant", label: "Tenant" },
                  { key: "score", label: "Score" },
                ]}
                rows={c.prioritized || []}
              />
              <ChangeTable
                title="Deprioritized"
                tone="dep"
                cols={[
                  { key: "login", label: "Agent", mono: true },
                  { key: "queue", label: "Queue", mono: true },
                ]}
                rows={c.deprioritized || []}
              />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
